import React, { useEffect, useState } from 'react';
import { PushNotifications } from '@capacitor/push-notifications';
import { ChevronRightIcon } from '@chakra-ui/icons';
import { Box, Center, Divider, Flex, Grid, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { SEHomeCard } from '../../common/components/SEHomeCard';
import { SlidingAnimationPageBase } from '../../common/components/SlidingAnimationPageBase';
import { IS_NATIVE } from '../../common/constants';
import { ArchiveIcon } from '../../styles/custom-icons';
import { LoadMoreNotificationsCard } from './LoadMoreNotificationsCard';
import { NotificationGroupingSelectMenu } from './NotificationGroupingSelectMenu';
import {
  getAllVisibleNotificationIds,
  useLoadMoreNotifications,
  useNotificationSearchMatcher,
} from './notifications-helper';
import { NotificationGrouping } from './notificationsApi';
import {
  NotificationsBulkActionBar,
  NotificationsEditButton,
  NotificationsSelectAllCheckbox,
} from './NotificationsBulkActions';
import { NotificationsListChronological } from './NotificationsListChronological';
import { NotificationsListPriorityStatus } from './NotificationsListPriorityStatus';
import { NotificationsListReadStatus } from './NotificationsListReadStatus';
import { NotificationsSearchInput } from './NotificationsSearchInput';
import { useNotificationsBulkEdit } from './use-notifications-bulk-edit';

type NotificationsHomeState = {
  groupBy: NotificationGrouping;
};

export function NotificationsList() {
  const [state, setState] = useState<NotificationsHomeState>({
    groupBy: getInitialGrouping(),
  });
  const { groupBy } = state;
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');

  // All notifications load into the one page — the query limit grows when "Load more" is clicked.
  // This query is deduplicated against the ones inside each list view component (same cache entry).
  const { data, limit, hasMore, isFetching, loadMore } = useLoadMoreNotifications(groupBy, false, searchQuery);
  const matchesSearch = useNotificationSearchMatcher(searchQuery);
  const allVisibleNotifications = getAllVisibleNotificationIds(data, groupBy, matchesSearch);

  const bulkEdit = useNotificationsBulkEdit(allVisibleNotifications);

  function getInitialGrouping() {
    const fromLocalStorage = localStorage.getItem('notificationsGroupBy');
    if (fromLocalStorage) {
      return fromLocalStorage as NotificationGrouping;
    }

    return 'CHRONOLOGICAL';
  }

  function handleUpdateGrouping(newGrouping: NotificationGrouping) {
    localStorage.setItem('notificationsGroupBy', newGrouping);

    setState({
      groupBy: newGrouping,
    });
  }

  /* istanbul ignore next -- @preserve */
  useEffect(() => {
    // Remove all delivered notifications, to remove the red badge on the native app home screen icon
    async function removeDeliveredNotifications() {
      const permStatus = await PushNotifications.checkPermissions();

      if (permStatus.receive === 'granted') {
        await PushNotifications.removeAllDeliveredNotifications();
      }
    }

    if (IS_NATIVE) {
      removeDeliveredNotifications();
    }
  }, []);

  const commonListProps = {
    groupBy,
    limit,
    searchQuery,
    isEditMode: bulkEdit.showAllCheckboxes,
    selectedIds: bulkEdit.selectedIds,
    onToggleSelect: bulkEdit.handleToggleSelectWithAutoExit,
    onSwipeSelect: bulkEdit.handleSwipeSelect,
  };

  const selectedGroupingToComponent: Record<NotificationGrouping, React.JSX.Element> = {
    CHRONOLOGICAL: <NotificationsListChronological {...commonListProps} />,
    UNREAD: <NotificationsListReadStatus {...commonListProps} />,
    PRIORITY: <NotificationsListPriorityStatus {...commonListProps} />,
  };

  return (
    <SlidingAnimationPageBase
      includeTopNavHamButton
      includeBottomNav
      backURL="../../home/device-status"
      title={t('Notifications.notifications')}
    >
      <Box px={3}>
        <Flex align="center" justify="space-between">
          <NotificationGroupingSelectMenu onSelectGrouping={handleUpdateGrouping} selectedGrouping={groupBy} />
          <NotificationsSearchInput onSearch={setSearchQuery} />
          <NotificationsEditButton bulkEdit={bulkEdit} />
        </Flex>

        <Divider mt={2} mb={4} />

        <NotificationsSelectAllCheckbox bulkEdit={bulkEdit} />

        {selectedGroupingToComponent[groupBy]}

        {hasMore && <LoadMoreNotificationsCard onClick={loadMore} isLoading={isFetching} />}

        <Divider my={4} />

        <SEHomeCard
          cursor="pointer"
          onClick={() => navigate(`../archive?direction=forward`)}
          borderTopLeftRadius="5px"
          mb={2}
          pl={0}
          as={'button'}
          w={'100%'}
          data-testid="view-archive-btn"
        >
          <Grid templateColumns={'75px 1fr'}>
            <Center>
              <ArchiveIcon w="2rem" h="2rem" />
            </Center>
            <Flex justify="space-between" align="center">
              <Text>{t('Notifications.view archive')}</Text>
              <ChevronRightIcon color={'customGrey.400'} w={'26px'} h={'26px'} />
            </Flex>
          </Grid>
        </SEHomeCard>
      </Box>

      <NotificationsBulkActionBar bulkEdit={bulkEdit} />
    </SlidingAnimationPageBase>
  );
}
